/**
 * Work the user asked for that the session could not carry out.
 *
 * Two situations put an action here, and both of them are situations where
 * throwing the work away would be a silent data loss:
 *
 * 1. A mutation came back `401` — the session died mid-flight.
 * 2. The tab is offline **and** past `expiresAt`, so the client already knows
 *    the mutation cannot succeed and must not be attempted (plan §2, "session
 *    expiry while offline": queued, not attempted, and visibly pending).
 *
 * ## Flush policy
 *
 * A queued **send** flushes automatically after re-auth: the user pressed Send,
 * that intent is unambiguous. Everything else — archive, delete, move, flag —
 * **asks first**, because an hour-old archive replayed without a word is a
 * surprise, and on a shared machine it may be a surprise performed by a
 * different person.
 *
 * ## Identity change
 *
 * If re-auth lands on a *different* account, the queue is discarded outright
 * and the caller says so. Replaying one user's archive into another user's
 * mailbox is the worst outcome available here.
 *
 * ## Why memory and not IndexedDB
 *
 * A queued action is a closure over live cache state, so it is not serialisable
 * as it stands; persisting it is the Outbox work in §10, which owns a real
 * durable draft store. What this module guarantees today is that nothing is
 * dropped *within* the tab's lifetime, and that the count is visible to the
 * user rather than implied.
 */

export type PendingActionKind = 'send' | 'other';

export interface PendingAction {
  readonly id: string;
  /** Shown to the user, e.g. "Archive 3 messages". Must be human-readable. */
  readonly label: string;
  readonly kind: PendingActionKind;
  readonly queuedAt: number;
  /** Re-attempt. Resolves on success; rejects to leave the action queued. */
  run(): Promise<void>;
}

export interface FlushResult {
  readonly flushed: PendingAction[];
  readonly failed: PendingAction[];
}

let seq = 0;

export class PendingActionQueue {
  private items: PendingAction[] = [];
  private readonly listeners = new Set<(items: readonly PendingAction[]) => void>();

  /** Queues an action and returns its id. */
  enqueue(input: Omit<PendingAction, 'id' | 'queuedAt'>): string {
    seq += 1;
    const id = `pending-${String(seq)}`;
    this.items = [...this.items, { ...input, id, queuedAt: Date.now() }];
    this.notify();
    return id;
  }

  list(): readonly PendingAction[] {
    return this.items;
  }

  get size(): number {
    return this.items.length;
  }

  /** Number of queued actions that will replay without asking (sends). */
  get automaticCount(): number {
    return this.items.filter((item) => item.kind === 'send').length;
  }

  subscribe(listener: (items: readonly PendingAction[]) => void): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Replays queued actions.
   *
   * `scope: 'automatic'` replays sends only — that is what runs straight after
   * a successful re-auth. `scope: 'all'` is the user answering the "replay
   * these?" prompt. Anything that rejects stays queued: a failed replay is
   * still outstanding work.
   */
  async flush(scope: 'automatic' | 'all'): Promise<FlushResult> {
    const candidates =
      scope === 'all' ? [...this.items] : this.items.filter((item) => item.kind === 'send');
    const flushed: PendingAction[] = [];
    const failed: PendingAction[] = [];

    for (const action of candidates) {
      try {
        await action.run();
        flushed.push(action);
      } catch {
        failed.push(action);
      }
    }

    const done = new Set(flushed.map((action) => action.id));
    this.items = this.items.filter((item) => !done.has(item.id));
    this.notify();
    return { flushed, failed };
  }

  /** Drops everything — used when re-auth lands on a different account. */
  discardAll(): PendingAction[] {
    const dropped = this.items;
    this.items = [];
    this.notify();
    return dropped;
  }

  private notify(): void {
    for (const listener of [...this.listeners]) listener(this.items);
  }
}

/** App-wide queue. Tests build their own instance instead of resetting this. */
export const pendingActions = new PendingActionQueue();
